import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, resolveImg, fmtRON, apiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AuthModal } from "@/components/AuthModal";
import { useAuth } from "@/context/AuthContext";
import { Star, MapPin, Users, BedDouble, Bath, Award, Grid3x3, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function PropertyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState(2);
  const [quote, setQuote] = useState(null);
  const [booking, setBooking] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/properties/${id}`).then(({ data }) => setProp(data)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (checkIn && checkOut) {
      api.post("/bookings/quote", { property_id: id, check_in: checkIn, check_out: checkOut, guests, origin_url: window.location.origin })
        .then(({ data }) => setQuote(data)).catch(() => setQuote(null));
    } else setQuote(null);
  }, [checkIn, checkOut, guests, id]);

  const handleBook = async () => {
    if (!user) return setAuthOpen(true);
    if (!checkIn || !checkOut) return toast.error("Selectează datele de check-in și check-out");
    setBooking(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        property_id: id, check_in: checkIn, check_out: checkOut, guests, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setBooking(false);
    }
  };

  const sendMessage = async () => {
    if (!user) return setAuthOpen(true);
    if (!msgText.trim()) return;
    setSending(true);
    try {
      await api.post("/messages/send", { property_id: id, host_id: prop.host_id, text: msgText });
      toast.success("Mesaj trimis gazdei!");
      setMsgOpen(false); setMsgText("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (loading) return (
    <div className="App"><Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <Skeleton className="h-10 w-2/3" /><Skeleton className="h-[420px] rounded-3xl" />
      </div>
    </div>
  );
  if (!prop) return <div className="App"><Navbar /><p className="text-center py-20">Proprietate inexistentă.</p></div>;

  const images = prop.images || [];
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{prop.title}</h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
          <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-[hsl(var(--amber))] text-[hsl(var(--amber))]" /> {prop.rating} · {prop.review_count} recenzii</span>
          {prop.is_superhost && <span className="flex items-center gap-1"><Award className="h-4 w-4 text-accent" /> Superhost</span>}
          <span className="flex items-center gap-1 text-muted-foreground"><MapPin className="h-4 w-4" /> {prop.location}</span>
        </div>

        {/* Gallery */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-4 lg:grid-rows-2 gap-2 rounded-3xl overflow-hidden h-[320px] lg:h-[480px] relative">
          <div className="lg:col-span-2 lg:row-span-2 h-full">
            <img src={resolveImg(images[0])} alt="" className="h-full w-full object-cover cursor-pointer" onClick={() => setGalleryOpen(true)} />
          </div>
          {images.slice(1, 5).map((im, i) => (
            <div key={i} className="hidden lg:block h-full">
              <img src={resolveImg(im)} alt="" className="h-full w-full object-cover cursor-pointer" onClick={() => setGalleryOpen(true)} />
            </div>
          ))}
          <Button onClick={() => setGalleryOpen(true)} data-testid="view-all-photos-button" variant="secondary"
                  className="absolute bottom-4 right-4 rounded-lg gap-2 bg-background shadow-md">
            <Grid3x3 className="h-4 w-4" /> Vezi toate fotografiile
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mt-10">
          {/* Left */}
          <div className="lg:col-span-2">
            <div className="pb-6 border-b border-border/60">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-2xl font-semibold">Găzduit de {prop.host?.name}</h2>
                  <p className="text-muted-foreground mt-1">
                    {prop.max_guests} oaspeți · {prop.bedrooms} dormitoare · {prop.beds} paturi · {prop.baths} băi
                  </p>
                </div>
                <Avatar className="h-14 w-14">
                  <AvatarFallback className="bg-primary text-primary-foreground text-lg">{prop.host?.name?.[0]}</AvatarFallback>
                </Avatar>
              </div>
            </div>

            <div className="py-6 border-b border-border/60">
              <p className="text-base leading-relaxed whitespace-pre-line">{prop.description}</p>
            </div>

            <div className="py-6 border-b border-border/60">
              <h3 className="font-heading text-xl font-semibold mb-4">Ce oferă locația</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {prop.amenities?.map((a) => (
                  <div key={a} className="flex items-center gap-3 text-sm">
                    <ShieldCheck className="h-5 w-5 text-accent" /> {a}
                  </div>
                ))}
              </div>
            </div>

            <div className="py-6">
              <Button onClick={() => setMsgOpen(true)} variant="outline" data-testid="message-host-button" className="rounded-full">
                Trimite mesaj gazdei
              </Button>
            </div>
          </div>

          {/* Booking widget */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-2xl border border-border shadow-xl p-6 bg-card" data-testid="property-booking-widget">
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-2xl font-semibold">{fmtRON(prop.price_per_night)}</span>
                <span className="text-muted-foreground">/ noapte</span>
              </div>

              <div className="grid grid-cols-2 rounded-xl border border-border overflow-hidden">
                <div className="p-3 border-r border-border">
                  <label className="overline text-muted-foreground text-[10px]">Check-in</label>
                  <input type="date" min={today} value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
                         data-testid="booking-checkin-input" className="w-full bg-transparent text-sm outline-none mt-1" />
                </div>
                <div className="p-3">
                  <label className="overline text-muted-foreground text-[10px]">Check-out</label>
                  <input type="date" min={checkIn || today} value={checkOut} onChange={(e) => setCheckOut(e.target.value)}
                         data-testid="booking-checkout-input" className="w-full bg-transparent text-sm outline-none mt-1" />
                </div>
              </div>
              <div className="p-3 rounded-xl border border-border mt-2">
                <label className="overline text-muted-foreground text-[10px]">Oaspeți</label>
                <select value={guests} onChange={(e) => setGuests(Number(e.target.value))} data-testid="booking-guests-select"
                        className="w-full bg-transparent text-sm outline-none mt-1 cursor-pointer">
                  {Array.from({ length: prop.max_guests }).map((_, i) => (
                    <option key={i} value={i + 1}>{i + 1} {i === 0 ? "oaspete" : "oaspeți"}</option>
                  ))}
                </select>
              </div>

              <Button onClick={handleBook} disabled={booking} data-testid="booking-submit-button"
                      className="w-full mt-4 h-12 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full text-base">
                {booking && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {quote ? `Rezervă · ${fmtRON(quote.total)}` : "Rezervă cu Stripe"}
              </Button>

              {quote && (
                <div className="mt-5 space-y-2 text-sm font-mono" data-testid="booking-breakdown">
                  <div className="flex justify-between text-muted-foreground">
                    <span>{fmtRON(quote.price_per_night)} × {quote.nights} nopți</span><span>{fmtRON(quote.base)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Taxă curățenie</span><span>{fmtRON(quote.cleaning_fee)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Taxă serviciu Airbnmountain</span><span>{fmtRON(quote.service_fee)}</span>
                  </div>
                  <div className="flex justify-between font-semibold pt-2 border-t border-border text-foreground">
                    <span>Total</span><span>{fmtRON(quote.total)}</span>
                  </div>
                </div>
              )}
              {!quote && <p className="text-xs text-center text-muted-foreground mt-3">Selectează datele pentru a vedea prețul total</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Other listings by host */}
      {prop.host_other_listings?.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-16" data-testid="host-other-listings">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-6">Alte cazări de la {prop.host?.name}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {prop.host_other_listings.map((o) => (
              <div key={o.id} onClick={() => { navigate(`/proprietate/${o.id}`); window.scrollTo(0, 0); }}
                   data-testid={`other-listing-${o.id}`}
                   className="cursor-pointer group">
                <div className="aspect-square rounded-2xl overflow-hidden mb-2">
                  <img src={resolveImg(o.images?.[0])} alt={o.title}
                       className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <p className="text-sm font-medium leading-tight line-clamp-1">{o.title}</p>
                <p className="text-xs text-muted-foreground">{o.region}</p>
                <p className="text-sm mt-0.5"><span className="font-semibold">{fmtRON(o.price_per_night)}</span> / noapte</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Gallery modal */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{prop.title}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {images.map((im, i) => <img key={i} src={resolveImg(im)} alt="" className="w-full rounded-xl object-cover" />)}
          </div>
        </DialogContent>
      </Dialog>

      {/* Message modal */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Mesaj către {prop.host?.name}</DialogTitle></DialogHeader>
          <Textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} rows={4} data-testid="host-message-textarea"
                    placeholder="Bună! Aș dori mai multe detalii despre..." />
          <Button onClick={sendMessage} disabled={sending} data-testid="send-host-message-button"
                  className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full">
            {sending && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Trimite mesaj
          </Button>
        </DialogContent>
      </Dialog>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <Footer />
    </div>
  );
}
